import {
  CountryCode,
  getCountries,
  getCountryCallingCode,
  parsePhoneNumberFromString,
} from 'libphonenumber-js';

import { getFormattingRegistry } from 'utils/formatting';

import {
  DEFAULT_COUNTRY_CODE,
  PHONE_MAX_DIGITS_BY_COUNTRY,
  PHONE_NUMBER_MAX_LENGTH,
} from './constant';

/**
 * Result of parsing a phone number value
 */
export interface PhoneNumberParseResult {
  countryCode: string;
  countryIsoCode?: CountryCode;
  displayValue: string;
  shouldUpdateField?: boolean;
  newFieldValue?: string;
}

/**
 * Result of converting display input into canonical storage value.
 */
export interface PhoneNumberStorageResult {
  e164Value: string;
  isValid: boolean;
}

export interface PhoneCountryOption {
  isoCode: CountryCode;
  callingCode: string;
}

/** Converts a persisted or displayed phone value to the canonical E.164 form. */
export const normalizePhoneNumberForStorage = (
  value: string | undefined,
  countryCode?: string,
): string => {
  if (!value) return '';

  let country: CountryCode | undefined;
  if (countryCode) {
    if (getCountries().includes(countryCode as CountryCode)) {
      country = countryCode as CountryCode;
    } else {
      country = getCountries().find(
        (candidate) => getCountryCallingCode(candidate) === countryCode,
      );
    }
  }
  const parsed = parsePhoneNumberFromString(value, country);
  if (parsed?.isValid()) return `+${parsed.countryCallingCode}${parsed.nationalNumber}`;

  const selectedCountryCode = countryCode ?? parsePhoneNumberValue(value).countryCode;
  return parseForStorage(value, selectedCountryCode).e164Value;
};

function getPhoneProvider() {
  return getFormattingRegistry().phone;
}

function getDefaultCountryCode(): string {
  return getPhoneProvider()?.getCountryCode() ?? DEFAULT_COUNTRY_CODE;
}

function resolveCallingCode(countryCode: string): string {
  return getCountries().includes(countryCode as CountryCode)
    ? getCountryCallingCode(countryCode as CountryCode)
    : countryCode;
}

/**
 * Gets max digit length for a country.
 */
export function getMaxDigitsForCountry(countryCode: string): number {
  return PHONE_MAX_DIGITS_BY_COUNTRY[countryCode] ?? PHONE_NUMBER_MAX_LENGTH;
}

/**
 * Returns supported country calling code options for the selector.
 * Keeps the list deterministic and guarantees the active/default code exists.
 */
export function getCountryCodeOptions(): PhoneCountryOption[] {
  return getCountries()
    .map((isoCode) => ({
      isoCode,
      callingCode: getCountryCallingCode(isoCode),
    }))
    .sort((first, second) => {
      const callingCodeDifference = Number(first.callingCode) - Number(second.callingCode);
      if (callingCodeDifference !== 0) return callingCodeDifference;

      // Native select type-ahead matches the option label. Put US first among
      // calling code 1 options so entering `1` resolves to the default country.
      if (first.isoCode === 'US') return -1;
      if (second.isoCode === 'US') return 1;
      return first.isoCode.localeCompare(second.isoCode);
    });
}

/**
 * Legacy US-only format fallback when provider doesn't support the country.
 */
function legacyFormatDisplay(digits: string): string {
  const { length } = digits;
  if (length < 4) return digits;
  if (length < 7) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  }
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)} - ${digits.slice(6)}`;
}

/**
 * Formats phone number for display using the formatting registry.
 */
export const formatPhoneNumberDisplay = (phoneNumber: string, countryCode?: string): string => {
  if (!phoneNumber) return '';

  let cc = resolveCallingCode(countryCode ?? getDefaultCountryCode());
  let digits = phoneNumber.replace(/\D/g, '');

  // Keep this boundary safe for every caller. Some browser autofill/native
  // change paths can briefly provide the persisted E.164 value to a display
  // formatter. The calling code must never be counted as national digits.
  if (phoneNumber.trim().startsWith('+')) {
    const parsedPhone = parsePhoneNumberFromString(phoneNumber);
    if (parsedPhone) {
      cc = parsedPhone.countryCallingCode;
      digits = parsedPhone.nationalNumber;
    }
  } else {
    // Also handle an international value that lost its leading "+" while
    // crossing a form/autofill boundary. A national number is already
    // bounded by its country's max length, so an overlong value beginning
    // with the selected calling code is the only safe case to unwrap.
    const callingCodeDigits = cc.replace(/\D/g, '');
    const maxDigits = getMaxDigitsForCountry(cc);
    if (
      callingCodeDigits &&
      digits.length > maxDigits &&
      digits.length <= maxDigits + callingCodeDigits.length &&
      digits.startsWith(callingCodeDigits)
    ) {
      digits = digits.slice(callingCodeDigits.length);
    }
  }

  digits = digits.slice(0, getMaxDigitsForCountry(cc));

  // E.164 values omit the UK national trunk prefix. Once the number is
  // complete, use libphonenumber's national representation to restore that
  // prefix and its spacing (for example, 2079460958 → 020 7946 0958).
  if (cc === '44') {
    const ukPhoneNumber = parsePhoneNumberFromString(digits, 'GB');
    if (ukPhoneNumber?.isValid()) return ukPhoneNumber.formatNational();
  }

  const provider = getPhoneProvider();
  if (provider) {
    const result = provider.formatDisplay(digits, cc);
    if (result) return result;
  }

  return legacyFormatDisplay(digits);
};

/**
 * Formats national input while the user types.
 * Phase 1 keeps existing behavior: strip non-digits and respect per-country max length.
 */
export const formatNationalAsYouType = (inputValue: string, countryCode: string): string => {
  return formatPhoneNumberDisplay(inputValue, countryCode);
};

/**
 * Converts phone number to E.164 format for storage.
 */
export const formatPhoneNumberForStorage = (
  phoneNumber: string,
  countryCode: string = getDefaultCountryCode(),
): string => {
  if (!phoneNumber) return '';

  const callingCode = getCountries().includes(countryCode as CountryCode)
    ? getCountryCallingCode(countryCode as CountryCode)
    : countryCode;
  const cleanPhoneNumber = phoneNumber
    .replace(/\D/g, '')
    .slice(0, getMaxDigitsForCountry(callingCode));
  const e164 = `+${callingCode}${cleanPhoneNumber}`;

  try {
    const parsed = parsePhoneNumberFromString(e164, countryCode as CountryCode);
    return parsed?.isValid() ? `+${parsed.countryCallingCode}${parsed.nationalNumber}` : '';
  } catch {
    return '';
  }
};

/**
 * Converts display input to canonical storage value on blur.
 * Country-aware parsing determines whether the value can be converted.
 */
export const parseForStorage = (
  displayValue: string,
  countryCode: string,
): PhoneNumberStorageResult => {
  if (!displayValue) {
    return { e164Value: '', isValid: false };
  }

  // The native change event used for browser autofill can normalize the
  // field to E.164 immediately before React's blur handler runs. Treating
  // that canonical value as national digits would prepend the calling code a
  // second time and truncate the number (for example, MX +525512345678).
  // Keep this conversion idempotent for all countries.
  if (displayValue.trim().startsWith('+')) {
    const parsedPhone = parsePhoneNumberFromString(displayValue);
    if (parsedPhone?.isValid()) {
      return {
        e164Value: `+${parsedPhone.countryCallingCode}${parsedPhone.nationalNumber}`,
        isValid: true,
      };
    }
  }

  const digits = displayValue.replace(/\D/g, '');
  const e164Value = formatPhoneNumberForStorage(digits, countryCode);
  return {
    e164Value,
    isValid: Boolean(e164Value),
  };
};

/**
 * Parses E.164 format phone number (e.g., "+15555555555")
 */
const parseE164PhoneNumber = (value: string): PhoneNumberParseResult => {
  const parsedPhone = parsePhoneNumberFromString(value);

  if (parsedPhone) {
    const countryCode = parsedPhone.countryCallingCode;
    const { nationalNumber } = parsedPhone;
    // E.164 contains the calling code, but the phone formatter expects the
    // national number separately. Passing the complete E.164 value through a
    // locale formatter can make it treat the calling code as user digits and
    // truncate/rearrange the number (for example, MX +525512345678 becoming
    // 525 512 3456). Keep the country context and format only nationalNumber.
    const displayValue = formatPhoneNumberDisplay(nationalNumber, countryCode);

    return {
      countryCode,
      countryIsoCode: parsedPhone.country,
      displayValue,
    };
  }

  const [, extractedCountryCode, phoneDigits] = value.match(/^\+(\d{1,3})(\d{6,15})$/) || [];
  if (extractedCountryCode && phoneDigits) {
    return {
      countryCode: extractedCountryCode,
      displayValue: formatPhoneNumberDisplay(phoneDigits, extractedCountryCode),
    };
  }

  return {
    countryCode: getDefaultCountryCode(),
    displayValue: '',
  };
};

/**
 * Parses legacy format phone number (e.g., "1-5555555555")
 */
const parseLegacyPhoneNumber = (value: string): PhoneNumberParseResult => {
  const [legacyCountryCode, legacyPhoneNumber] = value.split('-');
  if (legacyCountryCode && legacyPhoneNumber) {
    return {
      countryCode: legacyCountryCode,
      displayValue: formatPhoneNumberDisplay(legacyPhoneNumber, legacyCountryCode),
      shouldUpdateField: true,
      newFieldValue: `+${legacyCountryCode}${legacyPhoneNumber}`,
    };
  }

  return {
    countryCode: getDefaultCountryCode(),
    displayValue: '',
  };
};

/**
 * Parses display format or partial phone number.
 */
const parseDisplayPhoneNumber = (value: string): PhoneNumberParseResult => {
  const defaultCc = getDefaultCountryCode();

  return {
    countryCode: defaultCc,
    displayValue: value,
  };
};

const isE164Format = (value: string): boolean => value.startsWith('+');

const isLegacyFormat = (value: string): boolean =>
  value.includes('-') && /^\d-\d{6,15}$/.test(value);

/**
 * Main function to parse phone number value and extract components
 */
export const parsePhoneNumberValue = (value: string | undefined): PhoneNumberParseResult => {
  if (!value) {
    return {
      countryCode: getDefaultCountryCode(),
      displayValue: '',
    };
  }

  if (isE164Format(value)) {
    return parseE164PhoneNumber(value);
  }

  if (isLegacyFormat(value)) {
    return parseLegacyPhoneNumber(value);
  }

  return parseDisplayPhoneNumber(value);
};

/**
 * Explicit helper for hydrating UI state from persisted form values.
 * Alias kept to clarify intent in component code.
 */
export const parseStoredPhoneValue = (value: string | undefined): PhoneNumberParseResult =>
  parsePhoneNumberValue(value);

/**
 * Parses a browser-autofilled value directly to E.164.
 * Chrome typically provides international format (e.g. "+1 5551234567") which
 * libphonenumber-js handles without needing a country hint.
 */
export const parseAutofillPhone = (rawValue: string, countryCode: string): string => {
  const parsed = parsePhoneNumberFromString(rawValue);
  if (parsed?.isValid()) {
    return `+${parsed.countryCallingCode}${parsed.nationalNumber}`;
  }
  const storageResult = parseForStorage(rawValue, countryCode);
  return storageResult.isValid
    ? storageResult.e164Value
    : formatNationalAsYouType(rawValue, countryCode);
};

/**
 * Validates phone number.
 */
export const isValidPhoneNumber = (
  phoneNumber: string,
  countryCode: CountryCode | string = getDefaultCountryCode() as CountryCode,
): boolean => {
  if (!phoneNumber) return true;
  const cc = typeof countryCode === 'string' ? (countryCode as CountryCode) : countryCode;
  const phoneNumberObject = parsePhoneNumberFromString(phoneNumber, cc);
  return phoneNumberObject?.isValid() ?? false;
};

/**
 * Gets locale-aware phone placeholder.
 */
export const getPhonePlaceholder = (countryCode?: string): string => {
  const cc = countryCode ?? getDefaultCountryCode();
  const provider = getPhoneProvider();
  return provider?.getPlaceholder(cc) ?? '(000) 000 - 0000';
};
